import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../../store/appStore';
import Avatar from '../common/Avatar';

// Per-camera operator chat. Everyone who can access this camera shares one thread,
// backed by a chat WebSocket the backend broadcasts to. Mirrors the presence
// (CameraViewers) WS lifecycle: app-level keepalive + auto-reconnect on close.
// Anchored bottom-right of the stream; expanded by default, collapsible to a
// launcher button that shows an unread badge.
// Global chat-panel transparency preference (0–100%), set in Settings. Lower = more
// see-through. Shared key + live-update event so an open panel reflects changes.
export const CHAT_OPACITY_KEY = 'studio_chat_opacity';
export const CHAT_OPACITY_EVENT = 'studio-chat-opacity';
const DEFAULT_CHAT_OPACITY = 62;

// `inset` places both the collapsed launcher and the open panel. It is a prop because the
// on-screen PTZ mode covers the bottom edge and the right edge with controls, and only the
// parent knows which mode is active — see CameraView.
export default function ChatPanel({ cameraId, inset = { bottom: '16px', right: '16px' } }) {
  const { token, user } = useAppStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [opacity, setOpacity] = useState(
    () => parseInt(localStorage.getItem(CHAT_OPACITY_KEY) ?? String(DEFAULT_CHAT_OPACITY), 10)
  );

  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const pingRef = useRef(null);
  const listRef = useRef(null);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  useEffect(() => {
    let closed = false;

    function connect() {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/api/chat/${cameraId}?token=${token}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'history') {
            setMessages(data.messages || []);
          } else if (data.type === 'message' && data.message) {
            setMessages(prev => [...prev, data.message]);
            // Count unread only for others' messages while collapsed.
            if (collapsedRef.current && data.message.userId !== user?.id) {
              setUnread(u => u + 1);
            }
          }
        } catch (_) {}
      };
      ws.onopen = () => {
        pingRef.current = setInterval(() => {
          if (ws.readyState === 1) ws.send('ping');
        }, 25000);
      };
      ws.onclose = () => {
        if (pingRef.current) clearInterval(pingRef.current);
        if (!closed) retryRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    }

    connect();
    return () => {
      closed = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} }
    };
  }, [cameraId, token, user?.id]);

  // Live-update transparency when changed in Settings (same tab) or another tab.
  useEffect(() => {
    const onChange = (e) => {
      const v = e?.detail != null ? e.detail : parseInt(localStorage.getItem(CHAT_OPACITY_KEY) ?? String(DEFAULT_CHAT_OPACITY), 10);
      if (!Number.isNaN(v)) setOpacity(v);
    };
    window.addEventListener(CHAT_OPACITY_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHAT_OPACITY_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  // Keep the message list pinned to the latest message.
  useEffect(() => {
    if (!collapsed && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, collapsed]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: 'send', text }));
      setInput('');
    }
  }, [input]);

  const open = useCallback(() => { setCollapsed(false); setUnread(0); }, []);

  if (collapsed) {
    return (
      <button
        onClick={open}
        title="Open operator chat"
        style={{
          position: 'absolute', ...inset, zIndex: 25,
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 12px',
          background: 'rgba(8,12,18,0.88)',
          border: '1px solid var(--border-dim)',
          borderRadius: '999px',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
          fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        Chat
        {unread > 0 && (
          <span style={{
            minWidth: '17px', height: '17px', padding: '0 4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--accent-orange)', color: 'var(--bg-void)',
            borderRadius: '999px', fontSize: '10px', fontWeight: 800,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div style={{
      position: 'absolute', ...inset, zIndex: 25,
      width: '320px', height: '400px', maxHeight: 'calc(100% - 32px)',
      display: 'flex', flexDirection: 'column',
      background: `rgba(18,22,30,${(Math.min(100, Math.max(0, opacity)) / 100).toFixed(2)})`,
      border: '1px solid var(--border-dim)',
      borderRadius: 'var(--radius-md)',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: '1px solid var(--border-dim)',
        flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '7px',
          fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          Operator Chat
        </div>
        <button
          onClick={() => setCollapsed(true)}
          title="Collapse chat"
          style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px',
          }}
        >
          –
        </button>
      </div>

      {/* Messages */}
      <div ref={listRef} style={{
        flex: 1, overflowY: 'auto', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}>
        {messages.length === 0 ? (
          <div style={{
            margin: 'auto', textAlign: 'center', color: 'var(--text-muted)',
            fontSize: '12px', fontFamily: 'var(--font-display)',
          }}>
            No messages yet.
          </div>
        ) : messages.map(m => {
          const isMe = m.userId === user?.id;
          return (
            <div key={m.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <Avatar src={m.avatar} name={m.username} userId={m.userId} size={24} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700,
                    color: isMe ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '170px',
                  }}>
                    {isMe ? 'You' : m.username}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', flexShrink: 0 }}>
                    {new Date(m.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4,
                  wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                }}>
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div style={{
        display: 'flex', gap: '6px', padding: '8px',
        borderTop: '1px solid var(--border-dim)', flexShrink: 0,
      }}>
        <input
          className="input-field"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Message…"
          maxLength={2000}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="btn btn-primary" onClick={send} disabled={!input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
}
